import { useState, useEffect, useCallback } from 'react';

interface Size {
  width: number;
  height: number;
}

// Reference: https://github.com/martinstark/throttle-ts
export const throttle = <R, A extends never[]>(
  fn: (...args: A) => R,
  delay: number,
): [(...args: A) => R | undefined, () => void] => {
  let wait = false;
  let timeout: undefined | number;
  let cancelled = false;

  return [
    (...args: A) => {
      if (cancelled) return undefined;
      if (wait) return undefined;

      const val = fn(...args);

      wait = true;

      timeout = window.setTimeout(() => {
        wait = false;
      }, delay);

      return val;
    },
    () => {
      cancelled = true;
      clearTimeout(timeout);
    },
  ];
};

enum BreakPointsEnum {
  mobile = 0,
  smTablet = 550,
  tablet = 720,
  desktop = 1025,
  lgDesktop = 1440,
}

const breakpoints = [
  { name: 'mobile', width: BreakPointsEnum.mobile },
  { name: 'sm-tablet', width: BreakPointsEnum.smTablet },
  { name: 'tablet', width: BreakPointsEnum.tablet },
  { name: 'desktop', width: BreakPointsEnum.desktop },
  { name: 'lg-desktop', width: BreakPointsEnum.lgDesktop },
];

type ScreenSizeName = (typeof breakpoints)[number]['name'];

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<Size>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const [screen, setScreen] = useState<ScreenSizeName>('mobile');

  const isScreenSizeSmallerThan = (name: ScreenSizeName) => {
    return (
      breakpoints.findIndex((b) => b.name === screen) <
      breakpoints.findIndex((b) => b.name === name)
    );
  };
  const isScreenSizeLargerThan = useCallback(
    (name: ScreenSizeName) => {
      return (
        breakpoints.findIndex((b) => b.name === screen) >
        breakpoints.findIndex((b) => b.name === name)
      );
    },
    [screen],
  );
  useEffect(() => {
    function handleResize() {
      const wSize = window.innerWidth;
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      const reverseBreakpoints = [...breakpoints].reverse();
      const screenItem = reverseBreakpoints.find((b) => wSize >= b.width);
      if (screenItem) {
        setScreen(screenItem.name);
      }
    }

    const [delayHandleResize] = throttle(handleResize, 200);
    window.addEventListener('resize', delayHandleResize);
    handleResize();
    return () => window.removeEventListener('resize', delayHandleResize);
  }, []);

  return {
    windowSize,
    screen,
    BreakPointsEnum,
    isScreenSizeSmallerThan,
    isScreenSizeLargerThan,
  };
}
